import classNames from 'classnames'
import Link from 'components/link'
import { PERSONALIZE_YOUR_REGISTRY } from 'registry/constants'
import { registryPath, registryShopPath } from 'lib/urls'

import * as BLConstants from 'shared/constants'
// @ts-ignore
import pluralize from 'pluralize'
import { daysUntil } from 'lib/date'
import ShareUrl from 'registry/components/share-url'

import GhostLine from 'shared/GhostLine/GhostLine'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { REG_ITEM_RESERVATION_STATUS_PURCHASED } from 'registry/actions/reg-items-actions'
import RegistryAvatar from '../registry-avatar'
import RegistrySwitcher from '../RegistrySwitcher'
import { BabyRegistryActions } from '../BabyRegistryActions/BabyRegistryActions'
import { BirthdayWishlistActions } from '../BirthdayWishlistActions/BirthdayWishlistActions'

import css from './registry-sidenav.scss'

interface AddFilterParams {
  giftView: string | null
}

interface RegistrySidenavProps {
  activeNav: string
  addFilter: ({ giftView }: AddFilterParams) => void
  isEligibleForRegistryDiscount: boolean
  giftsPurchasedCount: number
  regItemsCount: number
  setActiveModal: (arg0: string) => void
}

const RegistrySidenav = ({
  activeNav,
  addFilter,
  isEligibleForRegistryDiscount,
  giftsPurchasedCount,
  regItemsCount,
  setActiveModal,
}: RegistrySidenavProps) => {
  const [currentUser] = useCurrentUser()
  const registryActions =
    currentUser?.currentRegistry?.type === null ? (
      <BabyRegistryActions
        activeNav={activeNav}
        registryUrlSlug={currentUser?.currentRegistry?.urlSlug}
      />
    ) : (
      <BirthdayWishlistActions
        activeNav={activeNav}
        registryUrlSlug={currentUser?.currentRegistry?.urlSlug}
      />
    )

  return (
    <div className={css.sidenav}>
      <div className={classNames(css.userContainer)}>
        <RegistryAvatar
          editable
          className={css.registryAvatar}
          url={currentUser?.currentRegistry?.photoUrl}
          onEdit={() => setActiveModal(PERSONALIZE_YOUR_REGISTRY)}
        />
        <div className={css.userInfo}>
          {!currentUser && (
            <>
              <GhostLine className={css.userInfoLoadingLine} />
              <GhostLine className={css.userInfoLoadingLine} />
            </>
          )}
          {currentUser && (
            <>
              {`Hi${
                currentUser?.firstName ? `, ${currentUser?.firstName}` : ''
              }!`}
              {currentUser?.family?.arrivalDate &&
              daysUntil(currentUser?.family?.arrivalDate) >= 1 ? (
                <div className="small flex center">
                  <span>
                    {daysUntil(currentUser?.family?.arrivalDate)}{' '}
                    {pluralize(
                      'day',
                      daysUntil(currentUser?.family?.arrivalDate)
                    )}{' '}
                    to go!
                  </span>
                  &nbsp;🎉
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <RegistrySwitcher />

      <ShareUrl
        label="Your Registry Link"
        slug={currentUser?.currentRegistry?.urlSlug}
      />

      {currentUser?.id === currentUser?.currentRegistry?.ownerId && (
        <DiscountMessage
          isEligibleForRegistryDiscount={isEligibleForRegistryDiscount}
        />
      )}

      <hr />
      {registryActions}

      <hr />
      <GiftCount
        count={regItemsCount}
        label="Gifts Added"
        url={registryPath(currentUser?.currentRegistry?.urlSlug)}
        onClick={() => addFilter({ giftView: null })}
      />
      <hr />
      <GiftCount
        count={giftsPurchasedCount}
        label="Gifts Purchased"
        url={registryPath(currentUser?.currentRegistry?.urlSlug)}
        onClick={() =>
          addFilter({ giftView: REG_ITEM_RESERVATION_STATUS_PURCHASED })
        }
      />
      <hr />
    </div>
  )
}

RegistrySidenav.defaultProps = {
  isEligibleForRegistryDiscount: false,
}

interface DiscountMessageProps {
  isEligibleForRegistryDiscount: boolean
}

const DiscountMessage = ({
  isEligibleForRegistryDiscount,
}: DiscountMessageProps) => {
  if (!isEligibleForRegistryDiscount) {
    return null
  }
  return (
    <div className="mvl xsmall text-center">
      ✨ Your{' '}
      <Link routerVersion={4} to={registryShopPath}>
        <b className="text-bold">
          {BLConstants.CONFIG.store.registryDiscountPctDisplay} registry{' '}
          discount
        </b>
      </Link>{' '}
      is ready!
    </div>
  )
}

interface GiftCountProps {
  count: number
  label: string
  onClick: () => void
  url: string
}

const GiftCount = ({ count, label, onClick, url }: GiftCountProps) => (
  <Link routerVersion={4} to={url} onClick={onClick}>
    <div className="h6 text-uppercase tiny text-bold text-light">{label}</div>
    <span className={classNames(css.active, 'h1')}>
      {count} <i className="fa fa-caret" />
    </span>
  </Link>
)

export default RegistrySidenav
